import type { CheckoutRequest } from '@telestore/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { formatMoney, normalizePhone, uuidv7 } from '@telestore/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { cart, useCart } from '../lib/cart.js';
import { booking, useBooking } from '../lib/booking.js';
import { useShop } from '../lib/shop-context.js';
import { haptic, isMiniApp, setMainButton, telegramUser } from '../lib/telegram.js';
import { ShopHeader, StickyBar } from '../components/chrome.js';
import { ErrorNotice, Money, Spinner } from '../components/primitives.js';
import { Button } from '../components/ui/button.js';
import { Input, Select, Textarea } from '../components/ui/field.js';
import { CalendarCheck, Store, Bike, UtensilsCrossed } from 'lucide-react';

/**
 * Checkout.
 *
 * §6.3 is the governing constraint: a customer arriving from a Facebook ad
 * must be able to complete this page without a Telegram account, without an
 * app install, and without creating a login. The Telegram handoff comes AFTER
 * the order exists, on the confirmation screen.
 */
export function CheckoutPage() {
  const { data, locale, t, customer } = useShop();
  const shop = data.shop;
  const { items, promotionCodes } = useCart();
  const navigate = useNavigate();

  const tgUser = telegramUser();
  const [name, setName] = useState(
    tgUser ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ') : '',
  );
  const [phone, setPhone] = useState('');
  const [provinceCode, setProvinceCode] = useState('');
  const [district, setDistrict] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [note, setNote] = useState('');
  const [paymentType, setPaymentType] = useState<'prepaid' | 'cod'>(
    shop.prepaidEnabled ? 'prepaid' : 'cod',
  );
  const [touched, setTouched] = useState(false);

  /**
   * Prefill from what this shop already knows about the Telegram buyer.
   *
   * Only into fields that are still empty: the record arrives after the first
   * render, and overwriting something they had already started typing would be
   * the form fighting them.
   */
  useEffect(() => {
    if (!customer) return;
    if (customer.name) setName((current) => current || customer.name!);
    if (customer.phone) setPhone((current) => current || customer.phone!);
  }, [customer]);

  /**
   * §4: the checkout FLOW is one of the four things a vertical is allowed to
   * change. Everything below branches on these two booleans rather than on
   * `shop.vertical` scattered through the JSX, so adding a fourth vertical
   * means adding a case here and nowhere else.
   */
  const isFnb = shop.vertical === 'fnb';
  const isServices = shop.vertical === 'services';

  /** The slot chosen on the product page; services orders have exactly one. */
  const slot = useBooking();

  const [fulfilmentMethod, setFulfilmentMethod] = useState<
    'ship' | 'pickup' | 'local_delivery' | 'dine_in' | 'appointment'
  >(isServices ? 'appointment' : isFnb ? 'pickup' : 'ship');

  /**
   * Who needs an address.
   *
   * §4 is explicit: an appointment has nowhere to deliver to, and an F&B
   * pickup or dine-in order has no address either. Only a shipped retail order
   * and a local F&B delivery do — and the F&B one needs a street, not a
   * province, because the food travels a few kilometres at most.
   */
  const needsAddress = fulfilmentMethod === 'ship' || fulfilmentMethod === 'local_delivery';
  const needsProvince = fulfilmentMethod === 'ship';

  /**
   * One idempotency key per checkout attempt, generated when the page mounts.
   * A customer on flaky 4G will tap "Place order" more than once; they get one
   * order.
   */
  const idempotencyKey = useRef(uuidv7());

  const shipping = useQuery({
    queryKey: ['shipping', shop.slug],
    queryFn: () => api.shipping(shop.slug),
    staleTime: 10 * 60_000,
  });

  const quote = useQuery({
    queryKey: ['quote', shop.slug, items, provinceCode, promotionCodes, fulfilmentMethod],
    /**
     * A retail order cannot be priced before we know the province, because
     * shipping is a per-province table (§3 `delivery`). A pickup or an
     * appointment has no such dependency, and making the customer choose a
     * province they will never travel to just to see a total would be absurd.
     */
    enabled: items.length > 0 && (!needsProvince || provinceCode !== ''),
    queryFn: () =>
      api.quote(shop.slug, {
        lines: cart.lines(),
        ...(needsProvince ? { provinceCode } : {}),
        promotionCodes,
        fulfilment: { method: fulfilmentMethod, slotStartsAt: null },
      }),
  });

  const phoneValid = useMemo(() => normalizePhone(phone) !== null, [phone]);
  const errors = {
    name: name.trim().length < 1 ? t('checkout.nameRequired') : null,
    phone: !phoneValid ? t('checkout.phoneInvalid') : null,
    address: needsAddress && addressLine.trim().length < 3 ? t('checkout.addressRequired') : null,
    province: needsProvince && provinceCode === '' ? t('checkout.selectProvinceFirst') : null,
    /**
     * A services order without a slot is not an order. It should be
     * unreachable — the product page will not add to the cart without one —
     * but the guard is cheap and the alternative is a booking for no time.
     */
    slot: isServices && !slot ? t('storefront.chooseTime') : null,
  };
  const valid = Object.values(errors).every((error) => error === null);

  const placeOrder = useMutation({
    mutationFn: () => {
      const body: CheckoutRequest = {
        lines: cart.lines(),
        promotionCodes,
        paymentType,
        // Sent only when there is somewhere to deliver to (§4).
        ...(needsAddress
          ? {
              delivery: {
                receiverName: name.trim(),
                phone: phone.trim(),
                /**
                 * A local F&B delivery has no province of its own — the shop's
                 * own moto is the courier. Stamping the shop's province keeps
                 * the snapshot complete without asking the customer.
                 */
                provinceCode: needsProvince ? provinceCode : LOCAL_PROVINCE,
                district: district.trim() || null,
                addressLine: addressLine.trim(),
                note: note.trim() || null,
              },
            }
          : {}),
        fulfilment: { method: fulfilmentMethod, slotStartsAt: null },
        ...(isServices && slot
          ? {
              booking: {
                resourceId: slot.resourceId,
                startsAt: slot.startsAt,
                ...(note.trim() ? { note: note.trim() } : {}),
              },
            }
          : {}),
        customer: { name: name.trim(), phone: phone.trim() },
        locale,
        channel: isMiniApp() ? 'telegram' : 'web',
        // §6.3: `?src=` from an ad link rides along into order attribution.
        attribution: readAttribution(),
        idempotencyKey: idempotencyKey.current,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      return api.checkout(shop.slug, body);
    },
    onSuccess: (result) => {
      haptic('success');
      cart.clear();
      // The slot is now the order's, not the browser's.
      booking.clear();
      void navigate({
        to: '/s/$slug/order/$code',
        params: { slug: shop.slug, code: result.order.code },
      });
    },
    onError: () => haptic('error'),
  });

  const submit = () => {
    setTouched(true);
    if (!valid || placeOrder.isPending) return;
    placeOrder.mutate();
  };

  useEffect(() => {
    if (!isMiniApp()) return;
    return setMainButton({
      text: t('checkout.placeOrder'),
      enabled: valid && !placeOrder.isPending,
      loading: placeOrder.isPending,
      onClick: submit,
    });
  });

  /**
   * Bounce back to the cart if the customer lands here with nothing in it.
   *
   * The `isIdle` guard is load-bearing: a successful checkout clears the cart
   * and then navigates to the order page, and without it this effect fires on
   * the now-empty cart and wins the race — showing "your cart is empty" to
   * somebody who has just been asked to transfer money.
   */
  useEffect(() => {
    if (items.length === 0 && placeOrder.isIdle) {
      void navigate({ to: '/s/$slug/cart', params: { slug: shop.slug } });
    }
  }, [items.length, placeOrder.isIdle, navigate, shop.slug]);

  const showError = (key: keyof typeof errors) => (touched ? errors[key] : null);

  return (
    <div className="min-h-dvh pb-32">
      <ShopHeader showBack />

      <div className="flex flex-col gap-6 px-4 pt-5">
        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{t('checkout.yourDetails')}</h2>
          <Field label={t('checkout.name')} error={showError('name')}>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
            />
          </Field>
          <Field label={t('checkout.phone')} error={showError('phone')}>
            <Input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="012 345 678"
            />
          </Field>
        </section>

        {/* §4 services: what was booked, restated before they commit. */}
        {isServices && slot ? (
          <section
            className="flex items-center gap-3 rounded-[var(--radius-card)] p-4"
            style={{ background: 'var(--color-surface)' }}
          >
            <CalendarCheck size={20} aria-hidden="true" style={{ color: 'var(--color-primary)' }} />
            <div>
              <p className="text-sm font-semibold">{t('storefront.appointment')}</p>
              <p className="text-sm tabular-nums opacity-80">{phnomPenhLabel(slot.startsAt)}</p>
            </div>
          </section>
        ) : null}

        {/* §4 F&B: pickup, a short local hop, or eating in. */}
        {isFnb ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-base font-semibold">{t('storefront.chooseFulfilment')}</h2>
            <div className="grid grid-cols-3 gap-2">
              <FulfilmentOption
                active={fulfilmentMethod === 'pickup'}
                onClick={() => setFulfilmentMethod('pickup')}
                icon={<Store size={18} aria-hidden="true" />}
                label={t('storefront.pickup')}
              />
              <FulfilmentOption
                active={fulfilmentMethod === 'local_delivery'}
                onClick={() => setFulfilmentMethod('local_delivery')}
                icon={<Bike size={18} aria-hidden="true" />}
                label={t('storefront.localDelivery')}
              />
              <FulfilmentOption
                active={fulfilmentMethod === 'dine_in'}
                onClick={() => setFulfilmentMethod('dine_in')}
                icon={<UtensilsCrossed size={18} aria-hidden="true" />}
                label={t('storefront.dineIn')}
              />
            </div>
          </section>
        ) : null}

        {needsAddress ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{t('checkout.deliveryAddress')}</h2>
          {needsProvince ? (
          <Field label={t('checkout.province')} error={showError('province')}>
            <Select
              value={provinceCode}
              onChange={(event) => setProvinceCode(event.target.value)}
            >
              <option value="">—</option>
              {shipping.data?.provinces.map((province) => (
                <option key={province.code} value={province.code}>
                  {locale === 'km' ? province.km : province.en}
                </option>
              ))}
            </Select>
          </Field>
          ) : null}
          <Field label={`${t('checkout.district')} (${t('common.optional')})`}>
            <Input
              value={district}
              onChange={(event) => setDistrict(event.target.value)}
            />
          </Field>
          <Field label={t('checkout.addressLine')} error={showError('address')}>
            <Textarea
              rows={2}
              value={addressLine}
              onChange={(event) => setAddressLine(event.target.value)}
            />
          </Field>
        </section>
        ) : null}

        {/*
          The note lives outside the address block: a pickup order has no
          address but very much has "no chilli, please".
        */}
        <section className="flex flex-col gap-3">
          <Field label={`${t('checkout.note')} (${t('common.optional')})`}>
            <Input value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">{t('checkout.paymentMethod')}</h2>
          {shop.prepaidEnabled ? (
            <PaymentOption
              active={paymentType === 'prepaid'}
              onClick={() => setPaymentType('prepaid')}
              title={t('checkout.payByTransfer')}
              hint={t('checkout.payByTransferHint')}
            />
          ) : null}
          {shop.codEnabled ? (
            <PaymentOption
              active={paymentType === 'cod'}
              onClick={() => setPaymentType('cod')}
              title={t('checkout.payOnDelivery')}
              hint={t('checkout.payOnDeliveryHint')}
            />
          ) : null}
        </section>

        <PromotionCodes
          codes={promotionCodes}
          invalid={quote.data?.invalidCodes ?? []}
          t={t}
        />

        <section className="card p-4">
          {quote.isPending && (!needsProvince || provinceCode) ? (
            <Spinner />
          ) : quote.data ? (
            <dl className="flex flex-col gap-2 text-sm">
              <Row label={t('common.subtotal')}>
                <Money amount={quote.data.subtotal.amount} currency={shop.currency} />
              </Row>
              {quote.data.adjustments.map((adjustment, index) => (
                <Row key={index} label={adjustment.label[locale] || adjustment.label.en}>
                  <Money amount={adjustment.amount} currency={shop.currency} />
                </Row>
              ))}
              <Row label={t('common.shipping')}>
                {quote.data.shipping.amount === 0 ? (
                  <span>{t('common.free')}</span>
                ) : (
                  <Money amount={quote.data.shipping.amount} currency={shop.currency} />
                )}
              </Row>
              <Row label={t('common.total')} strong>
                <Money amount={quote.data.total.amount} currency={shop.currency} />
              </Row>
              {quote.data.totalConverted ? (
                <p className="text-right text-xs opacity-55">
                  {t('storefront.referencePrice', {
                    amount: formatMoney(quote.data.totalConverted),
                  })}
                </p>
              ) : null}
            </dl>
          ) : (
            <p className="text-sm opacity-60">{t('checkout.selectProvinceFirst')}</p>
          )}

          {quote.data && quote.data.unavailableLines.length > 0 ? (
            <p className="mt-3 text-sm" style={{ color: 'var(--color-accent)' }}>
              {t('storefront.productUnavailable')}
            </p>
          ) : null}
        </section>

        {placeOrder.isError ? (
          <ErrorNotice
            message={
              placeOrder.error instanceof ApiError
                ? placeOrder.error.message
                : t('errors.internal')
            }
            retryLabel={t('common.retry')}
          />
        ) : null}
      </div>

      <StickyBar>
        <Button
          disabled={placeOrder.isPending}
          onClick={submit}
        >
          {placeOrder.isPending ? t('checkout.placing') : t('checkout.placeOrder')}
        </Button>
      </StickyBar>
    </div>
  );
}

/**
 * Promo codes (§3 `promotion`).
 *
 * The server is the only authority on whether a code applies — this component
 * never decides. It collects codes, shows which ones the last quote rejected,
 * and lets the customer take one back off.
 */
function PromotionCodes({
  codes,
  invalid,
  t,
}: {
  codes: string[];
  invalid: string[];
  t: ReturnType<typeof useShop>['t'];
}) {
  const [draft, setDraft] = useState('');

  const apply = () => {
    if (!draft.trim()) return;
    cart.addPromotionCode(draft);
    setDraft('');
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-semibold">{t('checkout.promoCode')}</h2>
      <div className="flex gap-2">
        <Input
          className="flex-1 uppercase"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
          if (event.key === 'Enter') {
          event.preventDefault();
          apply();
          }
          }}
          placeholder={t('checkout.promoPlaceholder')}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        <Button variant="ghost" width="auto" onClick={apply} disabled={!draft.trim()}>
          {t('checkout.applyCode')}
        </Button>
      </div>

      {codes.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {codes.map((code) => {
            const rejected = invalid.includes(code);
            return (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => cart.removePromotionCode(code)}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                  style={{
                    border: `1px solid ${rejected ? 'var(--color-accent)' : 'var(--color-primary)'}`,
                    color: rejected ? 'var(--color-accent)' : 'var(--color-primary)',
                    background: rejected ? 'transparent' : 'var(--color-primary-soft)',
                    // A rejected code stays visible with a strikethrough rather
                    // than vanishing — silently dropping it reads as a bug.
                    textDecoration: rejected ? 'line-through' : 'none',
                  }}
                >
                  {code}
                  <span aria-hidden>×</span>
                  <span className="sr-only">{t('common.remove')}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {invalid.length > 0 ? (
        <p className="text-xs" style={{ color: 'var(--color-accent)' }}>
          {t('checkout.promoNotApplied', { codes: invalid.join(', ') })}
        </p>
      ) : null}
    </section>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm opacity-75">{label}</span>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: 'var(--color-accent)' }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

function Row({
  label,
  children,
  strong,
}: {
  label: string;
  children: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'text-base font-bold' : ''}`}>
      <dt className={strong ? '' : 'opacity-70'}>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function PaymentOption({
  active,
  onClick,
  title,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-1 rounded-[var(--radius-card)] p-3 text-left"
      style={{
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
        background: active ? 'var(--color-primary-soft)' : 'transparent',
      }}
    >
      <span className="font-medium">{title}</span>
      <span className="text-xs opacity-65">{hint}</span>
    </button>
  );
}

/** Read `?src=` / `?ref=` captured when the customer first landed (§6.3). */
function readAttribution() {
  const params = new URLSearchParams(window.location.search);
  const stored = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('telestore.attribution') ?? 'null') as {
        source?: string;
        ref_code?: string;
      } | null;
    } catch {
      return null;
    }
  })();

  const source = params.get('src') ?? stored?.source ?? null;
  const ref = params.get('ref') ?? stored?.ref_code ?? null;
  if (!source && !ref) return undefined;

  return {
    source,
    ref_code: ref,
    campaign: params.get('campaign'),
    landing_path: window.location.pathname,
  };
}

/**
 * The province stamped on a local F&B delivery.
 *
 * §4 rules provincial shipping out for that vertical, so there is no province
 * question to ask — but `deliverySnapshot` still wants one, and Phnom Penh is
 * where a shop delivering by its own moto almost certainly is. If that stops
 * being true, the fix is a shop setting, not a question at checkout.
 */
const LOCAL_PROVINCE = 'PP';

/**
 * `DD/MM HH:MM` in Phnom Penh terms.
 *
 * §6.5: the offset is a fixed +7 with no DST, so this is arithmetic rather
 * than a reason to ship a timezone library to a phone on 3G.
 */
function phnomPenhLabel(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + 7 * 3_600_000);
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month} ${shifted.toISOString().slice(11, 16)}`;
}

function FulfilmentOption({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-[var(--radius-card)] px-2 py-3 text-xs font-medium"
      style={{
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
        background: active
          ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)'
          : 'transparent',
        color: active ? 'var(--color-primary)' : 'var(--color-text)',
      }}
    >
      {icon}
      {label}
    </button>
  );
}
